/**
 * Etiquetas en español para valores de enum que el backend expone en
 * inglés (risk_level, priority, severity, status). Centralizado aquí para
 * que cada chip de la app muestre el mismo texto — antes cada componente
 * (DocumentRecordsTab, RiskDocumentsTab, AlertsPanel, NotificationsBell,
 * RecommendationsTab/Panel, DocumentDetailDialog) renderizaba el valor
 * crudo del backend sin traducir, inconsistente con el resto de la UI.
 */

export const RISK_LEVEL_LABEL: Record<string, string> = {
  none: 'Ninguno',
  low: 'Bajo',
  medium: 'Medio',
  high: 'Alto',
  critical: 'Crítico',
}

export const PRIORITY_LABEL: Record<string, string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
  critical: 'Crítica',
}

export const SEVERITY_LABEL: Record<string, string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
  critical: 'Crítica',
}

export const DOCUMENT_STATUS_LABEL: Record<string, string> = {
  raw: 'Sin procesar',
  processed: 'Procesado',
  failed: 'Fallido',
}

/** Fase 8 (Strategy) — valores reales y exhaustivos de `Decision.status`
 * documentados en `app/decision_center/models.py`: "processing | open |
 * in_review | approved | dismissed | closed | failed". */
export const DECISION_STATUS_LABEL: Record<string, string> = {
  processing: 'Procesando',
  open: 'Abierta',
  in_review: 'En revisión',
  approved: 'Aprobada',
  dismissed: 'Descartada',
  closed: 'Cerrada',
  failed: 'Fallida',
}

/** UX-2.7 — corregido: SÍ existen transiciones reales de escritura
 * (`POST /decision/recommendations/{id}/accept|reject|implement`, PR-2A
 * Recommendation Lifecycle Closure, `app/decision_center/api/
 * endpoints.py`) — la nota anterior ("cero flujo de escritura") quedó
 * desactualizada frente al backend real. Los 4 valores de abajo son
 * exhaustivos (`Recommendation.status`, default "proposed" +
 * `transition_recommendation_status` solo acepta estos 3 destinos). */
export const RECOMMENDATION_STATUS_LABEL: Record<string, string> = {
  proposed: 'Propuesta',
  accepted: 'Aceptada',
  rejected: 'Rechazada',
  implemented: 'Implementada',
}

export const REPORT_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendiente',
  processing: 'Procesando',
  ready: 'Listo',
  success: 'Listo',
  failed: 'Fallido',
  error: 'Error',
}

/** Fallback seguro: si aparece un valor nuevo del backend sin traducir
 * todavía, se muestra tal cual en vez de "undefined". */
export function translateLabel(map: Record<string, string>, value: string): string {
  return map[value] ?? value
}

/**
 * UX-9 FASE 1.2 — `category` (Document/DocumentAnalysis/StrategicTopic)
 * NO es un enum cerrado como los de arriba: es texto libre asignado por
 * el clasificador de IPE. El vocabulario REAL y actual ya llega en
 * español con mayúsculas correctas ("Obra Pública", "Medio Ambiente",
 * `app/intelligent_processing/rules/defaults.py::DEFAULT_CATEGORIES`) —
 * eso se muestra tal cual, sin tocar. El único caso real encontrado de
 * texto crudo visible al cliente (revisión humana UX-9 Fase 1.2) viene
 * de un vocabulario MÁS ANTIGUO en snake_case/minúsculas
 * (`demo/generate_demo_data.py::CATEGORIAS`, anterior al vocabulario
 * actual del clasificador) — este mapa normaliza esos casos conocidos
 * (con acentos correctos) y, para cualquier otro valor no mapeado que
 * de cualquier forma llegara en snake_case, aplica Título Case como
 * último recurso honesto — nunca se muestra un guion bajo literal.
 * Consolida la lógica que antes vivía duplicada e incompleta (sin
 * acentos) en `v2/adapters/adaptRiskOverview.ts` y
 * `v2/adapters/adaptRiskPortfolio.ts`.
 */
const CATEGORY_LABEL_OVERRIDES: Record<string, string> = {
  obra_publica: 'Obra pública',
  medio_ambiente: 'Medio ambiente',
  educacion: 'Educación',
  transparencia: 'Transparencia',
  seguridad: 'Seguridad',
  salud: 'Salud',
  finanzas: 'Finanzas',
  general: 'General',
}

export function formatCategoryLabel(category: string | null | undefined): string {
  if (!category) return ''
  if (CATEGORY_LABEL_OVERRIDES[category]) return CATEGORY_LABEL_OVERRIDES[category]
  // Ya viene con mayúsculas reales del clasificador actual (p. ej.
  // "Obra Pública") — se muestra tal cual, sin tocar.
  if (!category.includes('_') && category !== category.toLowerCase()) return category
  return category
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}
